/**
 * Smart Prefetch Manager
 *
 * Manages intelligent prefetching queue that:
 * - Prioritizes currently visible series
 * - Automatically moves to next series when complete
 * - Re-prioritizes when user switches series
 * - Provides progress callbacks for UI
 * - Yields to user interactions for smooth scrolling
 *
 * It also owns the second TRANSPORT for those loads. Everything above decides
 * WHICH images to warm and WHEN, under gates that are already correct; the
 * transport below decides only HOW their bytes arrive. Where a retained bundle
 * chunk covers the images the window is about to request, that one chunk is
 * fetched first and the same `loadAndCacheImage` calls then resolve out of the
 * wadouri dataset cache as pure decode work. Nothing new decides how much to
 * fetch, and an image no chunk covers goes per object exactly as it always did
 * — which is what makes the fallback free: an unseeded key is just a key the
 * loader fetches the way it always has.
 *
 * "Fetched first" has one exception, and it is the common case on a cold open:
 * page delivery releases its rows before it fetches, so this loop is registered
 * against a series whose chunks are still streaming. A manifest already in
 * flight is JOINED rather than re-requested — the loader is the only place a
 * chunk is ever streamed, so its in-flight table is the whole truth about what
 * this tab is already paying for.
 *
 * See docs/planning/2026-08-25-bundle-aware-prefetch.md.
 */
import * as telemetry from '@/lib/telemetry';
import { cache, imageLoader } from '@cornerstonejs/core';
import parseImageId from '@cornerstonejs/dicom-image-loader/wadouri/parseImageId';
import { inFlightChunk, isSeriesBundlingDisabled, loadSeriesBundles, } from './bundleLoader';
import { chunkFor } from './bundleRegistry';
import { cachedDataSetSizeBytes, isDataSetLoaded } from './datasetSeed';
import { createImageIdsForImage } from './imageIds';
import { getDatasetCacheHighWaterBytes } from './init';
import { PREFETCH_WINDOW_SIZE } from './prefetchBudget';
// ─── Bundle transport ──────────────────────────────────────────────────────
//
// State that belongs to the STUDY rather than to any one series' loop, which is
// why it sits out here beside the singleton rather than on it: the high-water
// latch is a verdict about the tab's memory, and the fetched-chunk memo is
// about bytes that are already in a cache the series boundary means nothing to.
// Both are cleared by the manager's reset()/clear().
/**
 * The dataset-cache high-water latch — a one-way switch, not an eviction policy.
 *
 * Bundle bytes land in the wadouri dataset cache, which has no budget and no
 * eviction, and the cornerstone headroom test every speculative load answers to
 * cannot see them (it measures decoded pixels). For look-ahead series that is
 * still bounded — the prefetcher only asks for a chunk it was about to ask for
 * per object anyway, so the compressed shadow is smaller than the pixel working
 * set it shadows. The focused/visible series is where the bound breaks: it
 * deliberately ignores the headroom gate, so its bundle bytes would otherwise
 * grow to the high-water mark of everything the study ever prefetched.
 *
 * So: over the ceiling, bundle transport switches off for the rest of the study
 * and every series reverts to per object — which needs no code. Datasets the
 * pixel cache's eviction policy reaches are released with their images (see
 * ./imageCacheEviction); a bundle-seeded dataset no image has been decoded
 * from yet is invisible to that policy until one is, which is the asymmetry
 * this latch exists to bound.
 */
let bundleTransportDisabled = false;
/**
 * Chunk manifest URLs already fetched this study, whatever the outcome.
 *
 * A chunk is fetched at most once because its bytes outlive the window that
 * asked for them: a 100-object chunk seeds 100 datasets, of which one 24-image
 * window issues a quarter — the rest are still seeded when later top-ups reach
 * them, so re-fetching the chunk would re-download bytes already in the cache.
 * `failed` is memoized alongside `seeded` for the same reason it is not a
 * breaker trip: a 413 does not get smaller on a retry, and a shed chunk has
 * already spent its retries inside the loader. A `skipped` chunk is NOT
 * memoized — a cancel is not a verdict, and a later pass must be free to bundle
 * the same series again.
 */
const fetchedChunkUrls = new Set();
/** The chunk fetch currently in flight, if any. At most one, tab-wide. */
let activeChunkFetch = null;
/**
 * The dataset-cache key for one of the prefetcher's imageIds.
 *
 * The registry is keyed by cache key rather than by index because a chunk's
 * `offset`/`count` index into the PAGE's images while the prefetcher walks the
 * series' full id list — which is spliced, pinned and healed as pages land, so
 * a page-relative offset does not survive the trip. `parseImageId` is the
 * loader's OWN derivation (`datasetCacheKey` in ./datasetSeed runs the same
 * function on the same id), which is what makes the two sides unable to
 * disagree, and it strips the `?frame=` suffix — so every frame of a
 * multi-frame instance asks about the one chunk that covers its object.
 */
function datasetCacheKeyForImageId(imageId) {
    return parseImageId(imageId).url;
}
/** Abandon the in-flight chunk fetch, if any. It settles `skipped`. */
function abortActiveChunkFetch(reason) {
    if (!activeChunkFetch)
        return;
    activeChunkFetch.controller.abort(new DOMException(reason, 'AbortError'));
    activeChunkFetch = null;
}
/**
 * Abandon the in-flight chunk fetch when its series is no longer the one the
 * queue is about to run.
 *
 * Driven from the queue mutators (via `scheduleChunkGateEvaluation`, never
 * directly — see below) rather than polled, because the loop that owns the
 * fetch is parked awaiting it and cannot notice on its own. The loader
 * settles the chunk `skipped` and leaves the series' breaker CLOSED — being
 * deprioritized is the design working, not a fault — so the next pass over this
 * series bundles the very same chunk again.
 *
 * "The one the queue is about to run" is the whole gate, and it is NOT the
 * literal head of the queue — see the manager's `frontToRun`. It is asked for
 * lazily because answering it walks the queue: with no chunk in flight there is
 * nothing to abandon and nothing worth computing, which is the common case.
 *
 * Never called from a mutator directly — see `scheduleChunkGateEvaluation`, and
 * the two-pane case below for why the difference is not cosmetic.
 */
function abortChunkFetchIfDeprioritized(frontToRun) {
    if (activeChunkFetch && activeChunkFetch.seriesUID !== frontToRun()) {
        abortActiveChunkFetch('prefetch series deprioritized');
    }
}
/**
 * Session generation for the coalesced gate, bumped by teardown so a queued
 * evaluation belonging to a dead session cannot fire into the next one.
 */
let chunkGateGeneration = 0;
/** Whether an evaluation is already queued for the current mutator batch. */
let chunkGatePending = false;
/**
 * Evaluate the gate ONCE after the current synchronous run of queue mutators,
 * rather than once per mutator call.
 *
 * A run of mutators is one intent, and the queue only means anything once all
 * of it has landed. ViewerShell's registration effect re-announces the whole
 * visible set on every look-ahead page delivery —
 * `orderedVisible.forEach(prioritize)` with the ACTIVE viewport's series LAST
 * so it ends up at the front — and then re-queues the entire look-ahead order.
 * With one open pane that is a single call — the queue can still move (a
 * completed pane is unshifted back onto it), but `frontToRun()`'s ANSWER does
 * not. With two or more, the first call fronts another pane and the second fronts
 * the active one back again, so HALFWAY THROUGH the batch the queue genuinely
 * answers "the other pane runs next" — a state the shell never meant and holds
 * for a few microseconds. Evaluated per call, the gate reads that as a focus
 * switch and cancels whatever chunk is on the wire, at the pager's ~250ms
 * cadence, for as long as two panes are still loading. Measured on a two-pane
 * cold open: 2 chunk fetches, 2 aborts, 0 completions over 4 effect runs, with
 * the chunk's images then re-issued one object at a time.
 *
 * A microtask is the right seam because it is exactly "when the current
 * synchronous work finishes" — later than any mutator in the batch, sooner than
 * anything this batch itself goes on to await. (A continuation queued by a
 * PREVIOUS task can still run first; that's benign, because the evaluation then
 * judges the new chunk against the already-settled queue.) So a genuine switch
 * (one mutator fronting a different series that has work) still hands over
 * within the same tick, and an idempotent re-announcement settles back onto the
 * series that was already running and finds nothing to abort.
 *
 * The queue mutators keep kicking `processQueue` synchronously — that is a
 * separate job and not deferred here. On a real switch the abort is what
 * unparks the loop holding the chunk, and it does so INTO the `processQueue`
 * pass that is still running (its `isProcessing` latch means the mutator's own
 * kick was a no-op), which then advances to the new front on its own.
 */
function scheduleChunkGateEvaluation(frontToRun) {
    // When an evaluation is already pending, this call's `frontToRun` closure is
    // deliberately discarded — the earlier one fires instead. Correct only
    // because every caller passes the same `() => this.frontToRun()`; a future
    // caller wanting a DIFFERENT predicate must not assume its own runs.
    if (chunkGatePending)
        return;
    chunkGatePending = true;
    const generation = chunkGateGeneration;
    queueMicrotask(() => {
        // Torn down between the schedule and here: the queue this would ask about
        // belongs to a session that no longer exists, and any chunk it could abort
        // has already been abandoned by resetBundleTransport. Leave `pending` to
        // the teardown, which cleared it — this callback is the stale one.
        if (generation !== chunkGateGeneration)
            return;
        chunkGatePending = false;
        abortChunkFetchIfDeprioritized(frontToRun);
    });
}
/** Forget the latch and the memo. Study teardown / new study. */
function resetBundleTransport() {
    abortActiveChunkFetch('prefetch session ended');
    // Retire any queued gate evaluation with the session it was scheduled in, so
    // it cannot fire into the next one — and clear the latch, or the first
    // mutator of that next session would think an evaluation was already coming
    // and schedule nothing, silently disarming the gate for good.
    chunkGateGeneration++;
    chunkGatePending = false;
    bundleTransportDisabled = false;
    fetchedChunkUrls.clear();
}
/**
 * Mark every imageId a chunk covers as attempted, and report which marks this
 * call actually added.
 *
 * Ids whose bytes are on the way must not ALSO be requested per object, and
 * `attempted` is the seam that already says so — `nextUncached` skips it. The
 * marking spans the wait only: the caller un-marks exactly what it added, so a
 * chunk that comes back `failed` or `skipped` leaves its ids as retryable per
 * object as they were before, while an id already attempted this pass keeps
 * its mark and stays out of the reselection loop.
 */
function markChunkIdsAttempted(entry, attempted) {
    const marked = [];
    for (const image of entry.images) {
        for (const id of createImageIdsForImage(image)) {
            if (attempted.has(id))
                continue;
            attempted.add(id);
            marked.push(id);
        }
    }
    return marked;
}
/**
 * Wait for a chunk that is somebody ELSE's stream, giving up if `signal` fires.
 *
 * Resolves to the chunk's result, or to `undefined` when the wait was
 * abandoned. Abandoning is not cancelling: the stream it was waiting on belongs
 * to the page delivery that started it and keeps running, bytes and all. What
 * ends is only this loop's interest in the outcome, which is what lets a
 * deprioritized series hand over at once instead of at the mercy of a socket it
 * does not own.
 */
function settleOrAbandon(settled, signal) {
    if (signal.aborted)
        return Promise.resolve(undefined);
    return new Promise((resolve) => {
        const onAbort = () => {
            resolve(undefined);
        };
        signal.addEventListener('abort', onAbort, { once: true });
        void settled.then((result) => {
            signal.removeEventListener('abort', onAbort);
            resolve(result);
        });
    });
}
/**
 * Fetch the retained bundle chunk covering the batch the window is about to
 * issue, if there is one, if bundling is still the right transport, and if
 * nobody is fetching it already.
 *
 * Awaited deliberately, and that is what bounds concurrency: `processQueue`
 * already serializes series, so awaiting here means at most ONE bundle stream
 * per tab comes from the prefetch path, with no second scheduler. What the
 * caller issues afterwards is the batch — never the chunk's full coverage, and
 * minus whatever `partitionByStreamingChunks` defers to a stream that is
 * already carrying it. A chunk arrives
 * in whatever size the page that minted it asked for (24 for a background page,
 * 12/100 for a demand one, and a heal re-read re-arms the registry with
 * demand-shaped chunks), so starting every id a 100-object chunk covers would
 * put four windows of loads in flight and blow the pool cap the window size is
 * shared with. The remainder is not lost: those ids are seeded and later
 * top-ups issue them as decode-only work against a chunk this memo already
 * marks fetched.
 */
async function fetchCoveringChunk(seriesUID, batch, attempted, isLookahead) {
    if (bundleTransportDisabled || batch.length === 0)
        return;
    // The series' breaker, re-read here rather than trusted from retention time.
    // The registry gates retention on this same predicate, but it does so when
    // the PAGE was delivered — so chunks retained before the trip outlive it,
    // and without a second reading every window pass would fetch one of them,
    // re-fail it, and re-emit the breaker's telemetry for the rest of the study.
    // A disabled series is exactly as good as an uncovered one: per object, no
    // request, and no code behind the fallback.
    if (isSeriesBundlingDisabled(seriesUID))
        return;
    // One chunk, chosen by the id nearest the reader's focus. No covering chunk
    // (never retained, aged past its manifest's usable life, or a page that
    // declined to bundle at all) is indistinguishable here from "go per object".
    const entry = chunkFor(datasetCacheKeyForImageId(batch[0]));
    if (!entry || fetchedChunkUrls.has(entry.chunk.url))
        return;
    // These bytes are already here. The batch was selected against the PIXEL
    // cache, which cannot see compressed bytes sitting in the dataset cache
    // waiting to be decoded, so a window can land squarely inside a chunk this
    // tab has already downloaded — this series' own delivery streamed it, an
    // earlier window fetched it, or per-object XHRs happened to cover it — and
    // fetching it again roughly doubles a demand-delivered series' cold-open
    // bandwidth.
    //
    // Asked of the batch ids THIS chunk covers, not of the whole batch. A
    // 24-image window over a page minted 12-then-100 reaches past the boundary,
    // so "every id in the batch is seeded" is false for as long as the FAR chunk
    // is still arriving — and reading that as "the near chunk needs fetching"
    // re-downloads a chunk that is already complete. The ids beyond the boundary
    // answer to their own chunk, on a later pass.
    //
    // ALL seeded is the only case that skips: memoize the URL so later windows
    // over the same chunk do not re-ask either, and fall through to the loads
    // below, which are the decode-only work they already were. SOME seeded is an
    // aborted partial stream, and still fetches — `seedDataSet` early-returns on
    // a key that is already loaded, so re-seeding is idempotent and the fetch
    // completes the set rather than duplicating it.
    const covered = batch.filter((id) => chunkFor(datasetCacheKeyForImageId(id)) === entry);
    if (covered.every((id) => isDataSetLoaded(datasetCacheKeyForImageId(id)))) {
        fetchedChunkUrls.add(entry.chunk.url);
        return;
    }
    // Somebody is already streaming this exact manifest — almost always the
    // demand delivery of the page that minted it, which hands its rows to the
    // viewer BEFORE it fetches anything (lib/viewer/instancePageDelivery) and so
    // overlaps this loop by construction. Join that stream. The loader is the
    // only place a chunk is ever streamed, so a URL in its in-flight table is a
    // download this tab is already committed to, and opening a second one would
    // simply pay for the whole chunk twice.
    //
    // The high-water gate is deliberately NOT consulted first: it decides whether
    // to SPEND on a new stream, and waiting spends nothing. The bytes are coming
    // into the dataset cache either way, and the next window that wants a chunk
    // of its own is checked against a cache that has them.
    const streaming = inFlightChunk(entry.chunk.url);
    if (streaming) {
        const marked = markChunkIdsAttempted(entry, attempted);
        // Our own controller, registered as the active fetch even though we own no
        // request: it is what `abortChunkFetchIfDeprioritized` reaches for when the
        // queue's front moves, and abandoning the wait is the only thing this loop
        // is entitled to do to a stream it did not start.
        const controller = new AbortController();
        activeChunkFetch = { seriesUID, controller };
        try {
            const result = await settleOrAbandon(streaming, controller.signal);
            // `undefined` is the abandoned wait and `skipped` the abandoned stream;
            // neither is a verdict about the chunk, so neither is memoized and a
            // later pass is free to fetch it for real.
            if (result && result.status !== 'skipped')
                fetchedChunkUrls.add(entry.chunk.url);
        }
        finally {
            if (activeChunkFetch?.controller === controller)
                activeChunkFetch = null;
            for (const id of marked)
                attempted.delete(id);
        }
        return;
    }
    // The high-water gate, checked before each chunk fetch and never after the
    // fact: this is the one place bundle bytes enter the dataset cache, so it is
    // the only place the ceiling can be enforced. An override of `0` is the
    // per-machine kill switch and short-circuits before the cache is even read
    // — and before it is sampled for telemetry, which is why that override is
    // not itself reported as a gate trip below.
    const highWaterBytes = getDatasetCacheHighWaterBytes();
    if (highWaterBytes <= 0) {
        bundleTransportDisabled = true;
        return;
    }
    // Sampled on every check, not only the one that trips it, so telemetry sees
    // the study's whole high-water mark rather than just the final breach.
    const datasetCacheBytes = cachedDataSetSizeBytes();
    telemetry.recordDatasetCacheBytes(datasetCacheBytes);
    if (datasetCacheBytes >= highWaterBytes) {
        bundleTransportDisabled = true;
        telemetry.recordDatasetCacheGateTripped();
        return;
    }
    // Ids whose chunk fetch is in flight must not also be requested per object.
    const marked = markChunkIdsAttempted(entry, attempted);
    const controller = new AbortController();
    activeChunkFetch = { seriesUID, controller };
    try {
        const [handle] = loadSeriesBundles({
            // The series the manifest was minted for, which is what the loader's
            // breaker is keyed on — not the series whose window happens to want it.
            seriesId: entry.seriesId,
            // The registry retained the chunk's own member slice, so the offset that
            // indexed into the page it came from is re-based to 0 against it. `count`
            // stays as minted: a stream that frames past the slice is a manifest and
            // page disagreeing, which is exactly the framing fault the loader breaks
            // the circuit on.
            images: entry.images,
            bundles: [{ ...entry.chunk, offset: 0 }],
            // Look-ahead is speculative and competes in the capped background lane;
            // the focused/visible series is a viewport waiting on pixels and takes
            // the demand lane, which is never made to queue behind background.
            lane: isLookahead ? 'background' : 'demand',
            // Minted when the page was delivered and kept since, so it can be past
            // its signing TTL by the time we get here — the loader degrades this one
            // chunk to per object on a non-2xx instead of disabling the series.
            retained: true,
            signal: controller.signal,
        });
        const result = await handle.settled;
        if (result.status !== 'skipped')
            fetchedChunkUrls.add(entry.chunk.url);
    }
    catch (error) {
        // loadSeriesBundles settles rather than rejects, so this is belt and
        // braces; treat it like a failed chunk and let the images go per object.
        fetchedChunkUrls.add(entry.chunk.url);
        console.warn(`[Prefetch] Bundle chunk fetch failed for series ${seriesUID}:`, error);
    }
    finally {
        if (activeChunkFetch?.controller === controller)
            activeChunkFetch = null;
        for (const id of marked)
            attempted.delete(id);
    }
}
/**
 * Split the batch into the ids this pass may issue now, and the stream the rest
 * of them are already riding in on.
 *
 * `fetchCoveringChunk` resolves exactly ONE chunk — the one covering the id
 * nearest the reader's focus — and a window is wider than a chunk boundary is
 * far: 24 images against a demand page minted 12-then-100, so the FIRST window
 * of every demand series reaches across the first boundary. The ids on the far
 * side belong to a chunk the delivery's firehose already has on the wire, and
 * issuing them per object pays for those objects a second time — the exact
 * duplication the in-flight join exists to make unavailable, reappearing one
 * window later. Measured at 12 doubled objects per demand series on a cold
 * open, and up to 23 at a boundary further in.
 *
 * They are DEFERRED, not dropped, and the difference is the attempt-once
 * ledger: nothing here marks them, so the next top-up reselects them — by which
 * time their chunk has either seeded them (the load is decode-only) or settled
 * without them (the load goes per object, exactly as an uncovered id always
 * has). Either way it terminates, because a chunk leaves the loader's in-flight
 * table however it settles, cancels included.
 *
 * Two kinds of id are always issuable, and between them they are why a series
 * cannot starve here: one whose dataset is already cached — the bytes are HERE,
 * so the load is free whatever its chunk is doing — and one no retained chunk
 * covers, which has no stream to wait for and never had.
 */
function partitionByStreamingChunks(batch) {
    const issuable = [];
    let streaming;
    for (const id of batch) {
        const key = datasetCacheKeyForImageId(id);
        if (isDataSetLoaded(key)) {
            issuable.push(id);
            continue;
        }
        const entry = chunkFor(key);
        const onTheWire = entry && inFlightChunk(entry.chunk.url);
        if (!onTheWire) {
            issuable.push(id);
            continue;
        }
        streaming ??= onTheWire;
    }
    return { issuable, streaming };
}
/**
 * Park on a chunk somebody else is streaming, registering the wait as this
 * loop's active chunk fetch so a queue move can abandon it.
 *
 * The join inside `fetchCoveringChunk` is the usual way this loop waits on a
 * foreign stream. This is the other one, for the pass that deferred its ENTIRE
 * batch and has no load of its own left to race — without it that pass falls
 * through to the drain below, which reads an empty window as a finished series
 * and retires it with a chunk's worth of images never requested.
 */
async function awaitForeignChunk(seriesUID, streaming) {
    const controller = new AbortController();
    activeChunkFetch = { seriesUID, controller };
    try {
        await settleOrAbandon(streaming, controller.signal);
    }
    finally {
        if (activeChunkFetch?.controller === controller)
            activeChunkFetch = null;
    }
}
// Configuration for prefetch behavior
const PREFETCH_CONFIG = {
    // Rolling-window size: see prefetchBudget.ts for the rationale and the note
    // on why this must stay in sync with the Cornerstone image-load pool's cap.
    windowSize: PREFETCH_WINDOW_SIZE,
    // Pause duration when user is interacting (ms)
    interactionPauseDuration: 200,
    // How long after last interaction to consider user "idle" (ms)
    interactionIdleThreshold: 250,
    // Tier 2 (look-ahead) good-citizen ceiling. Background look-ahead of unopened
    // series only runs while the image cache is below this fraction of its max.
    // This keeps the speculative working set below the point where the cache
    // starts evicting (EVICTION_HIGH_WATER_FRACTION in ./imageCacheEviction), so
    // warming never pushes out what the radiologist recently looked at, and it
    // doubles as the large-study ceiling: on a 10k CT
    // look-ahead simply stops once the cache is ~85% full instead of unbounded
    // prefetch. Visible/prioritized series ignore this gate — they are first-class.
    lookaheadCacheCeiling: 0.85,
    // Status-notification throttle. notifyStatus → getStatus is O(series length)
    // and every subscriber re-renders on it, so notifying on EVERY settle made a
    // 1000-slice CT pay ~1000 O(n) scans + re-renders. Notify at most once per
    // this many settles or once per interval, whichever comes first; series
    // completion always notifies immediately.
    statusNotifyEverySettles: 8,
    statusNotifyIntervalMs: 250,
};
class PrefetchManager {
    queue = []; // Series UIDs in priority order
    activeSeriesUID = null;
    statusCallbacks = new Map();
    seriesImageIds = new Map();
    isProcessing = false;
    isStopped = false; // Flag to stop prefetching (set by clear() — also wipes)
    // Flag to PAUSE prefetching without wiping registrations (set by pause()). New
    // loads stop being issued; in-flight loads drain; queue/registrations survive so
    // resume() can pick up exactly where it left off. Distinct from clear() (wipes)
    // and reset() (fresh start). Used while MPR/fusion overlays are open.
    isPaused = false;
    lastInteractionTime = 0; // Track user interactions
    interactionListenersAttached = false;
    // Tier 2: series queued as background look-ahead (unopened series). Tracked
    // separately from the queue so the cache-headroom gate only throttles them,
    // never the visible/prioritized (Tier 0/1) series.
    lookaheadSeries = new Set();
    // Tier 0: per-series "focus" slice (the image index currently shown in a
    // viewport). Each series' uncached images are loaded center-out from this
    // index so the slices nearest the one the radiologist is looking at — and
    // the ones they're about to scroll into — load first.
    seriesFocusIndex = new Map();
    /**
     * Images this SESSION has already issued a load for, per series — the
     * attempt-once ledger `nextUncached` consults.
     *
     * Per series rather than per prefetchSeries CALL, and that is the whole
     * point. The guard exists so a persistently failing image is not reselected
     * forever (see nextUncached), but a call-scoped set only ever achieved
     * attempt-once *within one pass*: a failed load makes Cornerstone drop its
     * cache entry, so `cache.getImageLoadObject` goes falsy again, and the very
     * next pass re-selects it. Passes are not rare — ViewerShell re-runs its
     * registration effect on every `state.series` change and calls prioritize()
     * from it, which re-enters this loop — so on a cold open the count of
     * re-issues tracks the number of delivered pages. On a 29-series study that
     * measured 645 requests for a 23-image series, and it starved the look-ahead
     * tier (and therefore every retained bundle chunk) out of the whole cold
     * open.
     *
     * THE LEDGER RECORDS FAILURES, NOT ATTEMPTS, and the name is the only part
     * of it that says otherwise. A mark goes on when a load is issued and comes
     * back off the moment that load SUCCEEDS (see `startLoad`), so what survives
     * a pass is exactly the set of images this series tried and could not get.
     *
     * That distinction is the whole guard against stranding, and "a load that
     * succeeded is skipped by the cache test anyway" is not: the image cache
     * evicts least recently used first, so a perfectly good image is evicted as
     * soon as the working set the reader is scrolling needs the room, and
     * `cache.getImageLoadObject` goes falsy again exactly as it does after a
     * failure. Kept marked, such an image would never be selected again for the
     * rest of the session and its slice would stay blank. Unmarked on success, it
     * is simply uncached again, and the next pass reloads it.
     *
     * A persistently failing image still gets one attempt per series per session,
     * which is what the guard was for. The other recoverable case needs nothing
     * here: an instance whose pixels landed late and whose row is healed with a
     * real URL arrives as a DIFFERENT imageId, which was never attempted.
     */
    seriesAttempted = new Map();
    // Session epoch. Bumped by clear() and reset() so async loops parked across a
    // clear()→reset() boundary (awaiting a settle, a user idle, or a bundle chunk)
    // recognize they belong to a dead session and bail instead of topping up stale
    // series into the new session (isStopped alone can't distinguish this: reset()
    // sets it back to false, waking the stale loop as if nothing happened).
    sessionEpoch = 0;
    /**
     * Loads still in flight for series that have already HANDED OFF — see
     * `handsOffEarly`. Keyed by imageId; each value settles (never rejects) once
     * that load's own bookkeeping has run.
     *
     * Tagged with the tier the load was admitted under, because the two tiers
     * share the window differently (see `trailingSharedWith`):
     *
     * - A look-ahead series does not start while any visible load is in this
     *   map (see the Tier 2 gates in processQueue). Once running it counts
     *   EVERY handed-off load against its window, so background work never puts
     *   more than `windowSize` loads in flight in total — the shared budget is
     *   what lets the queue advance past a series without the advance being a
     *   second, unbounded window.
     * - A visible (Tier 0/1) series counts only the loads handed off by other
     *   visible series. The series the reader is looking at gets its whole
     *   window at once instead of one slot at a time as background loads land,
     *   and a background load that never settles cannot hold a slot from it.
     *
     * So total prefetch loads in flight peak at two windows: at most one of
     * visible work, plus at most one of look-ahead work admitted before a pane
     * opened.
     */
    trailingLoads = new Map();
    /**
     * Register imageIds for a series
     */
    registerSeries(seriesUID, imageIds) {
        this.seriesImageIds.set(seriesUID, imageIds);
        this.attachInteractionListeners();
    }
    /**
     * Attach interaction listeners to detect user activity
     */
    attachInteractionListeners() {
        if (this.interactionListenersAttached || typeof window === 'undefined')
            return;
        this.interactionListenersAttached = true;
        const markInteraction = () => {
            this.lastInteractionTime = Date.now();
        };
        // Track mouse and touch interactions
        window.addEventListener('wheel', markInteraction, { passive: true });
        window.addEventListener('mousedown', markInteraction, { passive: true });
        window.addEventListener('touchstart', markInteraction, { passive: true });
        window.addEventListener('keydown', markInteraction, { passive: true });
    }
    /**
     * Check if user is currently interacting
     */
    isUserInteracting() {
        return Date.now() - this.lastInteractionTime < PREFETCH_CONFIG.interactionIdleThreshold;
    }
    /**
     * Wait for user to stop interacting. Also returns early if the session is
     * stopped or superseded (epoch bumped by clear()/reset()) while parked —
     * callers re-check those flags after the await.
     */
    async waitForUserIdle(epoch) {
        while (this.isUserInteracting() && !this.isStopped && epoch === this.sessionEpoch) {
            await new Promise((resolve) => setTimeout(resolve, PREFETCH_CONFIG.interactionPauseDuration));
        }
    }
    /**
     * Wait for browser idle time using requestIdleCallback or a setTimeout
     * fallback. Only Tier 2 (look-ahead) work hops through this — see the
     * comment at its call site in prefetchSeries.
     */
    waitForIdle() {
        return new Promise((resolve) => {
            if ('requestIdleCallback' in window) {
                ;
                window.requestIdleCallback(() => resolve(), { timeout: 100 });
            }
            else {
                setTimeout(resolve, 10);
            }
        });
    }
    /**
     * Subscribe to status updates for a series
     * Returns unsubscribe function
     */
    onStatus(seriesUID, callback) {
        if (!this.statusCallbacks.has(seriesUID)) {
            this.statusCallbacks.set(seriesUID, new Set());
        }
        this.statusCallbacks.get(seriesUID).add(callback);
        // Immediately call with current status
        const status = this.getStatus(seriesUID);
        if (status) {
            callback(status);
        }
        // Return unsubscribe function
        return () => {
            const callbacks = this.statusCallbacks.get(seriesUID);
            if (callbacks) {
                callbacks.delete(callback);
            }
        };
    }
    /**
     * Add series to front of queue (high priority - user selected).
     *
     * This is the Tier 0/1 promotion path: a series assigned to a viewport calls
     * this, which both moves it to the front AND drops it out of the look-ahead
     * set so it loads as a first-class visible series (no cache-headroom gate).
     */
    prioritize(seriesUID) {
        // Promote out of background look-ahead — this is now a visible series.
        this.lookaheadSeries.delete(seriesUID);
        // Remove from current position if exists
        this.queue = this.queue.filter((uid) => uid !== seriesUID);
        // Add to front
        this.queue.unshift(seriesUID);
        // The deprioritized series' loop may be parked awaiting a bundle chunk and
        // cannot see the queue move from there — cancel it so the newly prioritized
        // series starts now rather than a chunk's worth of bytes later. Nothing is
        // cancelled when the batch this call belongs to did not actually change
        // which series runs next, which is most of them: callers re-prioritize
        // their whole visible set on every state change, one call per pane (see
        // frontToRun, and scheduleChunkGateEvaluation for why the BATCH rather than
        // the call is what the gate has to answer to).
        scheduleChunkGateEvaluation(() => this.frontToRun());
        this.processQueue();
    }
    /**
     * Tier 2 — Look-ahead. Queue the unopened series for low-priority background
     * prefetch, in the given order (see computeLookaheadOrder). These run only
     * after all visible (Tier 0/1) series ahead of them in the queue are done —
     * every image they asked for has landed or failed, not merely been
     * requested — and the user is idle, and only while the cache has headroom. Idempotent and
     * re-orderable: callers re-invoke this whenever the focused/visible set
     * changes so the look-ahead order tracks the radiologist's focus.
     *
     * Visible series are never demoted here — anything currently prioritized
     * stays ahead of the look-ahead tail.
     */
    enqueueLookahead(orderedSeriesUIDs) {
        const wanted = orderedSeriesUIDs.filter((uid) => this.seriesImageIds.has(uid) && !this.isSeriesComplete(uid));
        wanted.forEach((uid) => this.lookaheadSeries.add(uid));
        // Keep the visible (non-look-ahead) prefix exactly as-is, then rebuild the
        // look-ahead tail in the requested order. This lets focus changes re-order
        // pending look-ahead without disturbing the prioritized visible series.
        const visiblePrefix = this.queue.filter((uid) => !this.lookaheadSeries.has(uid));
        this.queue = [...visiblePrefix, ...wanted];
        scheduleChunkGateEvaluation(() => this.frontToRun());
        this.processQueue();
    }
    /**
     * Tier 0 — record which slice a series is currently showing so its prefetch
     * loads center-out from there (and re-centers as the user scrolls).
     */
    setSeriesFocusIndex(seriesUID, index) {
        if (Number.isFinite(index) && index >= 0) {
            this.seriesFocusIndex.set(seriesUID, index);
        }
    }
    /**
     * Tier 2 gate: is the image cache below the look-ahead ceiling? Visible
     * series ignore this; only background look-ahead pauses when it's false.
     */
    hasCacheHeadroom() {
        try {
            const max = cache.getMaxCacheSize();
            if (!max || max <= 0)
                return true;
            return cache.getCacheSize() < max * PREFETCH_CONFIG.lookaheadCacheCeiling;
        }
        catch {
            return true;
        }
    }
    /**
     * Tier 2 METADATA gate: may background look-ahead page a series' INSTANCE
     * ROWS in right now? True when the session is live, not paused behind an
     * overlay that owns its own loading (MPR / PET-CT fusion), and the user is
     * idle — deliberately WITHOUT the cache-headroom test canStartLookahead adds.
     *
     * Exposed for the work that has to happen BEFORE a series can be prefetched
     * at all: on a summary-loaded study the look-ahead window's series hold no
     * instances yet, so the viewer pages them in (ViewerShell) to get them
     * registered.
     *
     * The missing headroom test is the whole point of having two predicates.
     * Headroom is a PIXEL budget — it stops speculative image loads from evicting
     * the series the radiologist is reading. Paging instances spends none of it:
     * a few small JSON pages yielding rows, thumbnails and image ids, not decoded
     * pixels. Sharing one gate coupled the two budgets, and on a large CT the
     * pixel cache fills long before the window is paged, so every series past
     * that point kept empty tiles for the rest of the session — no rows, no
     * thumbnails, and nothing for the growth/heal machinery to see — even though
     * the reader was idle and the rows cost nothing. Registering a series while
     * the cache is full stays safe because every pixel-issuing path re-tests
     * headroom for itself (processQueue before it drives a look-ahead series, and
     * prefetchSeries on each pass of its window loop).
     */
    canPageLookahead() {
        return !this.isStopped && !this.isPaused && !this.isUserInteracting();
    }
    /**
     * Tier 2 PIXEL admission gate, as a public predicate: may background
     * look-ahead IMAGE LOADING start RIGHT NOW? Everything canPageLookahead
     * requires, plus the image cache being below the look-ahead ceiling and no
     * visible series' image still in flight — the same conditions
     * processQueue applies before driving a look-ahead series.
     */
    canStartLookahead() {
        return (this.canPageLookahead() &&
            this.hasCacheHeadroom() &&
            this.trailingSharedWith(false).length === 0);
    }
    /**
     * Add series to end of queue
     */
    enqueue(seriesUID) {
        if (!this.queue.includes(seriesUID)) {
            this.queue.push(seriesUID);
        }
        this.processQueue();
    }
    /**
     * Enqueue multiple series (for visible viewports)
     * These get priority over other queued series
     */
    enqueueVisible(seriesUIDs) {
        // Remove these from current queue positions
        const visible = new Set(seriesUIDs);
        const otherSeries = this.queue.filter((uid) => !visible.has(uid));
        // Visible series first, then others
        this.queue = [...seriesUIDs.filter((uid) => !this.isSeriesComplete(uid)), ...otherSeries];
        scheduleChunkGateEvaluation(() => this.frontToRun());
        this.processQueue();
    }
    /**
     * Enqueue remaining series in a study (lower priority)
     */
    enqueueStudySeries(seriesUIDs) {
        const queued = new Set(this.queue);
        seriesUIDs.forEach((uid) => {
            if (!queued.has(uid) && !this.isSeriesComplete(uid)) {
                this.queue.push(uid);
                queued.add(uid);
            }
        });
        this.processQueue();
    }
    /**
     * Check if a series has no more prefetch work left to dispatch: every image
     * is either already loaded or already in flight. This is a "should I keep
     * driving this series?" gate for the queue — NOT a completion signal — so it
     * deliberately uses cache.getImageLoadObject(), which Cornerstone3D makes
     * truthy the instant a load is registered (cache.putImageLoadObject, called
     * synchronously from imageLoader.loadAndCacheImage), rather than
     * cache.isLoaded(), which only flips once the image is actually decoded and
     * in the cache. Once every image is at least in flight there's nothing left
     * to dispatch, so processQueue/enqueue* correctly stop driving it further;
     * whether it has actually finished loading is answered by getStatus(),
     * which uses cache.isLoaded() so completion/telemetry never fires early.
     */
    isSeriesComplete(seriesUID) {
        const imageIds = this.seriesImageIds.get(seriesUID);
        if (!imageIds || imageIds.length === 0)
            return false;
        return imageIds.every((id) => cache.getImageLoadObject(id));
    }
    /** Has this series anything left for processQueue to dispatch? */
    hasDispatchableWork(seriesUID) {
        const imageIds = this.seriesImageIds.get(seriesUID);
        if (!imageIds || imageIds.length === 0)
            return false;
        return !this.isSeriesComplete(seriesUID);
    }
    /**
     * The series a fresh processQueue pass would drive first — the head of the
     * queue after skipping the entries processQueue would discard on sight.
     *
     * The distinction from `queue[0]` is what makes an in-flight bundle chunk
     * survivable, because a queue mutation that changes only the head is not a
     * change of focus. A series with nothing left to dispatch is shifted off and
     * skipped the instant the loop reaches it (via the shared hasDispatchableWork
     * predicate — see processQueue), so putting one at the head hands the front
     * to nobody: the series that was running is the series that resumes.
     *
     * Which matters because the viewer re-prioritizes idempotently, on a timer it
     * does not control. A cold open pages one look-ahead series' instances in
     * every few hundred ms; each delivery rebuilds the study's series state, the
     * shell's registration effect re-runs, and it re-prioritizes every open pane
     * and re-queues the whole look-ahead order whether or not anything moved. A
     * pane that has finished loading is no longer IN the queue, so re-prioritizing
     * it is a head change by the literal reading — and read that way it cancels
     * the one background chunk on the wire several times a second, forever: the
     * loop re-selects the same chunk, restarts it, and the next delivery kills it
     * again. Measured on a cold open as one chunk started 23 times in 6.1s with
     * zero completions, which is the look-ahead tier's transport not working at
     * all.
     *
     * "A fresh pass would drive" is deliberately not "processQueue IS driving
     * right now" — three states this function does not model make those
     * different questions:
     *  - paused or stopped: no pass runs at all, fresh or otherwise;
     *  - the look-ahead head is over the cache-headroom ceiling, or a visible
     *    series' load is still in flight: processQueue `break`s (see the Tier 2
     *    gates in processQueue) and drives nothing;
     *  - a pass is already `isProcessing`: this answers what the NEXT pass would
     *    drive, not what the parked current one is doing.
     * Aborting the active chunk against this answer is still correct in each
     * case. Over the ceiling, nothing is being driven either way, so aborting a
     * series other than the answer matches this file's pre-existing behavior
     * for that state. Paused is pre-existing and out of scope here too —
     * pause() never aborted an in-flight chunk before this change.
     *
     * Falls back to the literal head when nothing queued has work left, so the
     * answer is unchanged from a plain `queue[0]` in every case where there is
     * nothing to skip.
     */
    frontToRun() {
        return this.queue.find((uid) => this.hasDispatchableWork(uid)) ?? this.queue[0];
    }
    /**
     * Process the prefetch queue
     */
    async processQueue() {
        // Prevent concurrent processing. Paused means "hold, don't wipe" — bail here
        // and let resume() re-drive the queue (do NOT loop, or a paused series would
        // spin: prefetchSeries returns immediately while paused without shifting).
        if (this.isProcessing || this.queue.length === 0 || this.isStopped || this.isPaused)
            return;
        this.isProcessing = true;
        const epoch = this.sessionEpoch;
        while (this.queue.length > 0 &&
            !this.isStopped &&
            !this.isPaused &&
            epoch === this.sessionEpoch) {
            const seriesUID = this.queue[0];
            const imageIds = this.seriesImageIds.get(seriesUID);
            // Skip if no imageIds registered
            if (!imageIds || imageIds.length === 0) {
                this.queue.shift();
                continue;
            }
            // Skip if already complete — mirrors hasDispatchableWork, the same
            // predicate frontToRun uses, so the two cannot drift apart. "Complete"
            // here includes images somebody else is still downloading (the worklist
            // warm-up's first image of a pane, typically); a visible series adopts
            // those first, so the look-ahead gate below waits for them.
            if (!this.hasDispatchableWork(seriesUID)) {
                this.adoptForeignLoadsIfVisible(seriesUID, imageIds, epoch);
                this.queue.shift();
                this.notifyStatus(seriesUID);
                continue;
            }
            // Tier 2 gate: if the front series is a background look-ahead series and
            // the cache is at/over the ceiling, stop here. All visible (Tier 0/1)
            // series are ahead of look-ahead in the queue, so reaching a look-ahead
            // series means the visible working set is already served — pausing now
            // avoids evicting it. Leaves the series queued; a later prioritize/
            // enqueueLookahead (e.g. the user switching series) re-drives the queue.
            if (this.lookaheadSeries.has(seriesUID) && !this.hasCacheHeadroom()) {
                break;
            }
            // Tier 2 gate: look-ahead issues nothing while an image a visible series
            // asked for is still downloading. A small visible series hands off (see
            // handsOffEarly) the moment its loads are REQUESTED, so reaching a
            // look-ahead series does not by itself mean the open panes have their
            // pixels — and on a slow connection every load shares the pipe, so the
            // image the reader is looking at would arrive no sooner than the whole
            // study. Stop here, leave the series queued, and re-drive the queue when
            // the next visible load settles (succeeded or failed — either frees the
            // pipe); a pass that still finds one in flight stops again. Stopping
            // rather than parking keeps the queue open: a prioritize() meanwhile —
            // the user clicking an unopened series into a pane — runs at once.
            if (this.lookaheadSeries.has(seriesUID)) {
                const visibleInFlight = this.trailingSharedWith(false);
                if (visibleInFlight.length > 0) {
                    const redrive = () => {
                        if (epoch === this.sessionEpoch)
                            void this.processQueue();
                    };
                    this.nextTrailingSettle(visibleInFlight).then(redrive, redrive);
                    break;
                }
            }
            this.activeSeriesUID = seriesUID;
            // A series that hands off early returns as soon as it has dispatched
            // everything; its loads finish under `trailingLoads` (see handsOffEarly).
            // react-doctor-disable-next-line react-doctor/async-await-in-loop -- the queue drains one series at a time, in priority order, under the cache budget
            const completed = await this.prefetchSeries(seriesUID, imageIds, epoch);
            // Superseded mid-flight by clear()/reset(): the new session owns all
            // state now (including isProcessing) — touch nothing on the way out.
            if (epoch !== this.sessionEpoch)
                return;
            this.activeSeriesUID = null;
            // A series' registered id list can GROW while its loop is running: paged
            // instance loading (components/viewer/ViewerContext) delivers a page at a
            // time and re-registers the series on each one, and live ingest widens it
            // again later. `imageIds` above was captured before those landed, so
            // shifting the series off here would retire it with its tail never
            // dispatched — and nothing re-drives it, because prioritize() during
            // processing is a no-op (isProcessing). Re-read the registration and keep
            // the series at the front instead; the next pass picks up the new ids and
            // nextUncached skips everything already loaded or in flight.
            const registeredNow = this.seriesImageIds.get(seriesUID);
            const grewWhileLoading = !!registeredNow && registeredNow.length > imageIds.length;
            // Only remove from queue if we completed (not interrupted by prioritize)
            // Check if this series is still at front of queue
            if (!this.isStopped && completed && !grewWhileLoading && this.queue[0] === seriesUID) {
                this.queue.shift();
            }
        }
        this.isProcessing = false;
    }
    /** This series' attempt-once ledger, created on first use. */
    attemptedFor(seriesUID) {
        const existing = this.seriesAttempted.get(seriesUID);
        if (existing)
            return existing;
        const created = new Set();
        this.seriesAttempted.set(seriesUID, created);
        return created;
    }
    /**
     * Pick the next `count` still-uncached images for a series, ordered center-out
     * from its focus slice (Tier 0). The displayed slice and its neighbours load
     * first, expanding outward; default focus 0 means a plain front-to-back load.
     * Recomputed on every window top-up so it re-centers when the user scrolls
     * mid-load, and returns fewer than `count` (or none) as the series fills.
     */
    nextUncached(seriesUID, imageIds, attempted, count) {
        if (count <= 0)
            return [];
        const focus = this.seriesFocusIndex.get(seriesUID) ?? 0;
        const uncached = [];
        for (let idx = 0; idx < imageIds.length; idx++) {
            const id = imageIds[idx];
            // Skip already-cached images, and images this SESSION has already tried
            // and FAILED for this series — without that guard a persistently-failing
            // image would be reselected forever, since we recompute the selection on
            // every top-up AND on every re-drive of the series. The ledger drops its
            // mark on success (see `startLoad`), so an image the cache later evicts
            // is uncached again rather than stranded, and gets reloaded. It also
            // keeps us from re-issuing images that are still in flight (they are
            // marked when their load starts, and unmarked only at a successful
            // settle). Center-out re-selection is untouched for images not yet tried.
            // Deliberately cache.getImageLoadObject() here, not cache.isLoaded(): a
            // dispatch decision must treat "already in flight" the same as "already
            // loaded" so we never issue a duplicate request for the same image (e.g.
            // one registered by an earlier, now-deprioritized call to prefetchSeries
            // for this same series — see the re-entrant case in isSeriesComplete).
            // The bundle transport marks a chunk's ids attempted for the duration of
            // its fetch and un-marks them on every outcome, so an id is skipped here
            // while its bytes are already on the way and is free to be reselected the
            // moment that chunk settles, seeded or not (see fetchCoveringChunk).
            if (!attempted.has(id) && !cache.getImageLoadObject(id)) {
                uncached.push({ id, dist: Math.abs(idx - focus) });
            }
        }
        uncached.sort((a, b) => a.dist - b.dist);
        return uncached.slice(0, count).map((o) => o.id);
    }
    /**
     * May this series hand off to the next queued one while its own loads are
     * still in flight?
     *
     * A series that fits in one window, with no retained bundle chunk covering
     * any of it. That is every series below the backend's minimum bundled series
     * size — on an X-ray study, every series: one large image each. Such a
     * series dispatches its whole id list in its first top-up, and then all the
     * loop has left to do is wait. Kept strictly serial, the queue could advance
     * only after that one image downloaded AND decoded, so a 28-series study
     * loaded one image at a time with the decode workers and the connection
     * mostly idle. Handing off lets the next series start while the previous
     * ones finish, under a shared window budget (see `trailingLoads`).
     *
     * "The next series" means the next VISIBLE one: two open panes download at
     * once. A look-ahead series waits until every visible series' handed-off
     * load has settled (see the Tier 2 gates in processQueue), so background
     * work never competes with the images on screen for the connection.
     *
     * Anything with chunk coverage keeps the serial shape exactly: bundle
     * coordination (`activeChunkFetch`, the deprioritize abort, the one-stream
     * bound in `fetchCoveringChunk`) assumes one series runs at a time, and a
     * series that hands off never touches a chunk, so that invariant is
     * untouched. So does anything larger than a window, which fills the window
     * by itself and would gain nothing — CT and MR stacks load as before.
     */
    handsOffEarly(imageIds) {
        return (imageIds.length <= PREFETCH_CONFIG.windowSize &&
            !imageIds.some((id) => chunkFor(datasetCacheKeyForImageId(id))));
    }
    /**
     * Take over a series' in-flight loads so its loop can return. Each load
     * keeps its own bookkeeping — the ledger mark comes off on success inside
     * `startLoad` already — and here additionally logs a failure and notifies
     * the series' status on every settle, so the series' completion (and the
     * telemetry hanging off it) fires when its last load lands, exactly as it
     * would had the loop stayed to watch. The series fits one window, so a
     * notify per settle is at most a window's worth of O(window) scans. A
     * visible series also adopts its images' loads that somebody else started
     * (see adoptForeignLoads), so it leaves every one of its unlanded images in
     * the visible tier's trailing set, whoever requested it.
     */
    handOff(seriesUID, imageIds, inFlight, epoch, lookahead) {
        for (const [id, load] of inFlight)
            this.trackTrailing(seriesUID, id, load, epoch, lookahead);
        inFlight.clear();
        if (!lookahead)
            this.adoptForeignLoads(seriesUID, imageIds, epoch);
    }
    /**
     * Record one handed-off load in `trailingLoads` under the tier it was
     * admitted at: the entry comes off when it settles, a failure is logged, and
     * the series' status is notified — unless the session it belongs to is dead.
     * `load` must never reject (it resolves to its error instead).
     */
    trackTrailing(seriesUID, id, load, epoch, lookahead) {
        const trailing = load.then(({ error }) => {
            if (this.trailingLoads.get(id)?.settled === trailing)
                this.trailingLoads.delete(id);
            // A dead session: clear()/reset() already dropped the callbacks and
            // the series, and nothing of this one is reported into the next.
            if (epoch !== this.sessionEpoch || this.isStopped)
                return;
            if (error)
                this.reportLoadFailure(id, error);
            this.notifyStatus(seriesUID);
        });
        this.trailingLoads.set(id, { settled: trailing, lookahead });
    }
    /**
     * A visible series takes over the loads of its own images that somebody else
     * started and that have not landed yet — in practice the worklist warm-up
     * (studyWarmup), which requests each pane's first image when a study row is
     * expanded, often still downloading on a slow connection when the viewer
     * opens. To the dispatch predicate such an image is already taken
     * (cache.getImageLoadObject), so this series issues nothing for it and,
     * without this, would record nothing either: look-ahead would start at once
     * and share the connection with the very image on screen. Adopted, the load
     * counts as one of the visible tier's handed-off loads, so the Tier 2 gate in
     * processQueue holds look-ahead until it settles, and the series' completion
     * still fires when its bytes land. Ids already tracked (including the loads
     * this series just handed off) and images already loaded are left alone.
     *
     * Only a visible series of hand-off shape (see handsOffEarly) adopts: a
     * look-ahead series is what the gate holds, and a larger series keeps its
     * own serial shape.
     */
    adoptForeignLoadsIfVisible(seriesUID, imageIds, epoch) {
        if (this.lookaheadSeries.has(seriesUID) || !this.handsOffEarly(imageIds))
            return;
        this.adoptForeignLoads(seriesUID, imageIds, epoch);
    }
    /** The adoption itself (see adoptForeignLoadsIfVisible); visible series only. */
    adoptForeignLoads(seriesUID, imageIds, epoch) {
        for (const id of imageIds) {
            if (this.trailingLoads.has(id) || cache.isLoaded(id))
                continue;
            const loadObject = cache.getImageLoadObject(id);
            if (!loadObject)
                continue;
            this.trackTrailing(seriesUID, id, loadObject.promise.then(() => ({}), (error) => ({ error })), epoch, false);
        }
    }
    /**
     * The handed-off loads that hold slots of a series' window: every one for a
     * look-ahead series, only the visible tier's own for a visible series (see
     * `trailingLoads`).
     */
    trailingSharedWith(lookahead) {
        const shared = [];
        for (const trailing of this.trailingLoads.values()) {
            if (lookahead || !trailing.lookahead)
                shared.push(trailing.settled);
        }
        return shared;
    }
    /** Settles when any of the given handed-off loads settles. Only called with some. */
    nextTrailingSettle(shared) {
        return Promise.race(shared).then(() => null);
    }
    /** A load failure nothing else acts on: logged, and the loop moves on. */
    reportLoadFailure(id, error) {
        // Sentry capture happens via the central IMAGE_LOAD_FAILED listener
        // (captureViewerError.ts) — do not also capture here.
        console.warn(`[Prefetch] Failed to load ${id}:`, error);
    }
    /**
     * Prefetch all images in a series using a ROLLING WINDOW: keep up to
     * PREFETCH_CONFIG.windowSize loads in flight at once and start the next as each
     * one settles, instead of the old fixed batch + Promise.allSettled barrier that
     * paced the whole series by its slowest member. A hung/slow image therefore ties
     * up only its one slot and never stalls the rest. Center-out order, Tier-2
     * cache-headroom gating, deprioritization, interaction-yielding, and pause all
     * still hold — we just refill the window continuously instead of in lockstep.
     *
     * @returns true if completed (or cache-saturated), false if interrupted/paused
     */
    async prefetchSeries(seriesUID, imageIds, epoch) {
        const isLookahead = this.lookaheadSeries.has(seriesUID);
        // Nothing left for this call to dispatch: every image is either already
        // loaded or already in flight (cache.getImageLoadObject — see
        // isSeriesComplete for why that, not cache.isLoaded, is the right
        // predicate for a dispatch decision). notifyStatus reports the real load
        // state via cache.isLoaded, so this doesn't claim completion just because
        // everything has been requested. processQueue checks the same predicate
        // right before calling here and adopts any of those still downloading
        // under somebody else's request (see adoptForeignLoads), so this branch
        // only guards a caller that skipped that check.
        if (imageIds.every((id) => cache.getImageLoadObject(id))) {
            this.notifyStatus(seriesUID);
            return true;
        }
        // Notify initial status
        this.notifyStatus(seriesUID);
        // Status-notify throttle state (see PREFETCH_CONFIG.statusNotify*): getStatus
        // is O(series), so per-settle notifications are batched.
        let settlesSinceNotify = 0;
        let lastNotifyAt = Date.now();
        // Images we've started loading (in flight, loaded, or failed) so we don't
        // re-select them on subsequent center-out top-ups — see nextUncached. Read
        // from the SERIES' ledger rather than started empty, so a re-drive of this
        // series does not re-issue what an earlier pass already tried and failed
        // (see seriesAttempted for why that mattered so much).
        const attempted = this.attemptedFor(seriesUID);
        const windowSize = Math.max(1, PREFETCH_CONFIG.windowSize);
        const handsOff = this.handsOffEarly(imageIds);
        // In-flight loads keyed by imageId. Each entry resolves (never rejects) to the
        // id plus any error, so Promise.race tells us which one settled without a
        // slow/hung load blocking the others.
        const inFlight = new Map();
        const startLoad = (id) => {
            attempted.add(id);
            inFlight.set(id, imageLoader.loadAndCacheImage(id).then(() => {
                // Succeeded, so the mark comes off: attempt-once is a rule about
                // FAILURES, and a load that worked has nothing left to protect
                // against. While the image sits in the cache the cache test skips
                // it anyway, so this changes nothing until the cache evicts it —
                // and at that moment it is the difference between an image the
                // next pass reloads and one stranded for the session.
                // A rejection deliberately keeps its mark.
                attempted.delete(id);
                return { id };
            }, (error) => ({ id, error })));
        };
        while (!this.isStopped && epoch === this.sessionEpoch) {
            // Deprioritized: another series jumped to the front of the queue. Bail at
            // once so the prioritized series starts without waiting for our window to
            // drain; the loads already in flight finish in the background (Cornerstone
            // dedupes/caches them, so no wasted work). A bundle chunk in flight is the
            // one thing that is NOT left to finish — the gate evaluation the batch
            // that moved the front scheduled aborted it, and the loader settles it
            // `skipped` with the series' breaker closed, so whatever bytes arrived
            // stay seeded and a later pass fetches the same chunk again.
            if (this.queue[0] !== seriesUID && this.queue.length > 0) {
                // react-doctor-disable-next-line react-doctor/js-set-map-lookups -- queue holds a handful of series UIDs; checked once per loop on the deprioritize path
                if (!this.queue.includes(seriesUID)) {
                    this.queue.push(seriesUID);
                }
                // A series that may hand off hands its loads over here too, so its
                // completion is still reported when they land (and they count against
                // the shared window while they do).
                if (handsOff)
                    this.handOff(seriesUID, imageIds, inFlight, epoch, isLookahead);
                return false;
            }
            // Tier 2 good-citizen: a background look-ahead series stops issuing new
            // loads the moment the cache crosses the ceiling, so it never evicts the
            // visible working set. In-flight loads still drain below.
            let lookaheadBlocked = isLookahead && !this.hasCacheHeadroom();
            // Top up the rolling window unless paused, blocked, or yielding to the user.
            if (!lookaheadBlocked && !this.isPaused) {
                // Yield to interaction (scroll/pan/zoom); returns immediately when idle.
                await this.waitForUserIdle(epoch);
                if (this.isStopped || epoch !== this.sessionEpoch)
                    return false;
                // Look-ahead ALSO yields to a busy main thread before claiming slots:
                // a requestIdleCallback hop keeps speculative fetching of unopened
                // series off the critical path. The focused/visible series deliberately
                // does NOT hop — it is what the radiologist is waiting on, and
                // throttling it behind idle callbacks is what left the connection empty
                // between batches. Speed first for Tier 0/1.
                if (isLookahead) {
                    await this.waitForIdle();
                    if (this.isStopped || epoch !== this.sessionEpoch)
                        return false;
                }
                // The queue is the third thing that can move while parked — the other
                // two being `this.isStopped` and `epoch !== this.sessionEpoch`, each
                // already re-checked above after both awaits — and unlike those two it
                // must not be answered by falling through: a series
                // the front has passed while we waited would otherwise open a bundle
                // chunk fetch that the very next gate evaluation is bound to abort —
                // the gate answers to `frontToRun` and this series is no longer it. The
                // batch that moved the front lands as one synchronous run of mutators,
                // so by here it has fully settled and this is not a half-applied
                // reading of it. Same condition as the deprioritize check at the top of
                // the loop, which is where the `continue` hands over.
                if (this.queue.length > 0 && this.queue[0] !== seriesUID)
                    continue;
                // Re-check the gates that can flip while parked waiting for idle: a
                // pause() landing mid-park (MPR/fusion opening on click) must NOT be
                // followed by a full-window top-up, and a look-ahead series' cache
                // headroom may have run out. Skip the top-up and fall through to the
                // drain logic below instead.
                lookaheadBlocked = isLookahead && !this.hasCacheHeadroom();
                if (!lookaheadBlocked && !this.isPaused) {
                    // The window is shared with the loads of series that handed off
                    // ahead of this one — all of them for look-ahead, only the visible
                    // tier's for a visible series (see trailingLoads).
                    const batch = this.nextUncached(seriesUID, imageIds, attempted, windowSize - inFlight.size - this.trailingSharedWith(isLookahead).length);
                    // This series' own load clock (perf_series_load) starts at its first
                    // real dispatch — the first time it has a slot and something to put
                    // in it, whether the bytes then come by chunk or per object. Not on
                    // entry: a series parked waiting for a slot, or held at the look-ahead
                    // ceiling or by a pause and re-driven later, would report that wait
                    // as load time. Idempotent per series, so a re-drive keeps the clock.
                    if (batch.length > 0)
                        telemetry.markSeriesLoadDispatched(seriesUID);
                    // TRANSPORT, not admission. WHICH images this pass wants is already
                    // decided and nothing below un-decides it: where a retained chunk
                    // covers them, that one chunk is fetched first and the very same
                    // loads become decode-only; where none does — no manifest, an expired
                    // one, the high-water latch — this is a no-op and they go per object
                    // exactly as they always have. The only thing transport moves is
                    // WHEN, for an id whose bytes are already on the wire.
                    await fetchCoveringChunk(seriesUID, batch, attempted, isLookahead);
                    // Two things can have moved while we were parked on that fetch, and
                    // both are the same two the idle park above re-checks. A dead session
                    // exits; a queue whose front has moved (which is what aborted the
                    // chunk) loops round so the deprioritize check at the top hands over
                    // at once, instead of issuing a window of loads for a series nobody
                    // is waiting on any more.
                    if (this.isStopped || epoch !== this.sessionEpoch)
                        return false;
                    if (this.queue.length > 0 && this.queue[0] !== seriesUID)
                        continue;
                    // The batch can reach past the chunk resolved above and into one the
                    // delivery is still streaming; those ids arrive with it and must not
                    // also be requested per object (see partitionByStreamingChunks).
                    const { issuable, streaming } = partitionByStreamingChunks(batch);
                    issuable.forEach(startLoad);
                    // Nothing of ours running and nothing issuable — the whole window is
                    // waiting on somebody else's bytes. Park on that stream instead of
                    // falling through to the drain below, which would read the empty
                    // window as a finished series.
                    if (inFlight.size === 0 && streaming) {
                        await awaitForeignChunk(seriesUID, streaming);
                        if (this.isStopped || epoch !== this.sessionEpoch)
                            return false;
                        continue;
                    }
                    // Everything this series has is now dispatched: hand its loads off
                    // and let the queue move on (see handsOffEarly). Returned as done,
                    // like any series with nothing left to dispatch — its completion is
                    // reported as those loads settle.
                    if (handsOff &&
                        inFlight.size > 0 &&
                        this.nextUncached(seriesUID, imageIds, attempted, 1).length === 0) {
                        this.handOff(seriesUID, imageIds, inFlight, epoch, isLookahead);
                        return true;
                    }
                }
            }
            if (inFlight.size === 0) {
                // Nothing running and nothing new scheduled this pass.
                // Left queued (not completed) when blocked/paused so a later
                // resume/enqueue/prioritize re-drives it; complete otherwise.
                if (lookaheadBlocked || this.isPaused)
                    return false;
                // The shared window is full of loads handed off by the series ahead
                // of this one, so the top-up could take nothing — that is waiting for
                // a slot, not being done. Park until one frees.
                const shared = this.trailingSharedWith(isLookahead);
                if (shared.length > 0 &&
                    this.nextUncached(seriesUID, imageIds, attempted, 1).length > 0) {
                    await this.nextTrailingSettle(shared);
                    if (this.isStopped || epoch !== this.sessionEpoch)
                        return false;
                    continue;
                }
                // Series completion always notifies (throttling may have swallowed the
                // last few settles' updates).
                this.notifyStatus(seriesUID);
                return true; // every image is cached or has been attempted
            }
            // Wait for the next in-flight load to settle, then loop to top up. A
            // handed-off load this series shares its window with settling frees a
            // slot just the same, so it wakes the loop too (and needs nothing else
            // from it).
            const shared = this.trailingSharedWith(isLookahead);
            const settled = await Promise.race(shared.length > 0
                ? [...inFlight.values(), this.nextTrailingSettle(shared)]
                : inFlight.values());
            if (this.isStopped || epoch !== this.sessionEpoch)
                return false;
            if (!settled)
                continue;
            inFlight.delete(settled.id);
            if (settled.error) {
                const errorMessage = settled.error instanceof Error ? settled.error.message : String(settled.error);
                // react-doctor-disable-next-line react-doctor/js-set-map-lookups -- String.includes (substring match), not array membership
                if (errorMessage.includes('CACHE_SIZE_EXCEEDED')) {
                    // Off-screen images are evicted ahead of every insert that needs the
                    // room (./imageCacheEviction; Cornerstone's own LRU skips nearly
                    // everything the wadouri loader files), so hitting this means the
                    // cache is genuinely saturated by what is on screen, in a volume, or
                    // still loading. Don't purgeCache() — that wipes the images the user
                    // is viewing and thrashes. Just stop background-prefetching this
                    // series; the viewport's own loads stay first-class.
                    //
                    // Bailing here does NOT write the window off. Those loads are still
                    // in flight and still own their own settle: each one that succeeds
                    // drops its own ledger mark (see `startLoad`), whether or not this
                    // loop is still around to watch it. Only the id that actually failed
                    // keeps its mark, which is what a failure does everywhere else — one
                    // attempt per image per series, no window-wide exception.
                    console.warn('[Prefetch] Cache saturated, pausing background prefetch for this series');
                    this.notifyStatus(seriesUID); // treated as completion — always notify
                    return true;
                }
                this.reportLoadFailure(settled.id, settled.error);
            }
            // Notify progress, throttled: at most once per N settles or per interval
            // (whichever fires first). Completion paths above notify unconditionally.
            if (!this.isStopped) {
                settlesSinceNotify++;
                const now = Date.now();
                if (settlesSinceNotify >= PREFETCH_CONFIG.statusNotifyEverySettles ||
                    now - lastNotifyAt >= PREFETCH_CONFIG.statusNotifyIntervalMs) {
                    this.notifyStatus(seriesUID);
                    settlesSinceNotify = 0;
                    lastNotifyAt = now;
                }
            }
        }
        return false;
    }
    /**
     * Notify all callbacks for a series
     */
    notifyStatus(seriesUID) {
        const status = this.getStatus(seriesUID);
        if (!status)
            return;
        const callbacks = this.statusCallbacks.get(seriesUID);
        if (callbacks) {
            callbacks.forEach((cb) => cb(status));
        }
    }
    /**
     * Get current prefetch status for a series
     */
    getStatus(seriesUID) {
        const imageIds = this.seriesImageIds.get(seriesUID);
        if (!imageIds)
            return null;
        const total = imageIds.length;
        // Deliberately cache.isLoaded(), not cache.getImageLoadObject(): this is a
        // completion/progress predicate (feeds telemetry.completeSeriesLoad via
        // onStatus), and getImageLoadObject() is truthy the instant a load is
        // registered — before the image is actually decoded and in the cache (see
        // cache.putImageLoadObject/cache.isLoaded in @cornerstonejs/core). Using it
        // here previously reported a series "loaded" while up to a full prefetch
        // window's worth of requests were still outstanding.
        const loaded = imageIds.filter((id) => cache.isLoaded(id)).length;
        return {
            seriesUID,
            total,
            loaded,
            complete: loaded >= total,
        };
    }
    /**
     * Check if a series is registered
     */
    isRegistered(seriesUID) {
        return this.seriesImageIds.has(seriesUID);
    }
    /**
     * Whether a series is queued as background Tier 2 look-ahead (vs a
     * prioritized visible/open series). Used by the UI to render look-ahead
     * progress more subtly than the active series' loading.
     */
    isLookahead(seriesUID) {
        return this.lookaheadSeries.has(seriesUID);
    }
    /**
     * Pause prefetching WITHOUT wiping registrations. Stops issuing new loads
     * (in-flight loads drain on their own); the queue, registered series, focus
     * indices, look-ahead set, and status subscriptions all survive so resume()
     * continues exactly where it left off.
     *
     * Use this while a modal that owns its own volume loading is open (MPR /
     * PET-CT fusion) so background stack prefetch yields to it — unlike clear(),
     * which wipes every registration and leaves prefetch dead for the rest of the
     * viewer session.
     */
    pause() {
        this.isPaused = true;
    }
    /**
     * Resume prefetching after pause(). No-op if not paused. Re-drives the queue
     * from wherever it stood.
     */
    resume() {
        if (!this.isPaused)
            return;
        this.isPaused = false;
        this.processQueue();
    }
    /**
     * Clear all queues and stop prefetching
     */
    clear() {
        this.sessionEpoch++; // orphan any loop still parked on an await
        // Includes a loop parked on a chunk fetch, which the epoch alone cannot
        // wake: cancel it so it settles at once, and drop the study-scoped
        // transport state (latch + fetched-chunk memo) with it.
        resetBundleTransport();
        this.isStopped = true;
        this.isPaused = false;
        this.queue = [];
        this.activeSeriesUID = null;
        this.isProcessing = false;
        this.lastInteractionTime = 0;
        this.lookaheadSeries.clear();
        this.seriesFocusIndex.clear();
        this.seriesAttempted.clear();
        this.trailingLoads.clear();
        // Clear callbacks to prevent memory leaks
        this.statusCallbacks.clear();
        this.seriesImageIds.clear();
    }
    /**
     * Reset the stopped state (call when starting a new study)
     */
    reset() {
        // Bump the epoch so stale loops from a pre-clear() session can't wake up
        // (isStopped is false again after this) and top up into the new session.
        this.sessionEpoch++;
        // A new study re-arms bundle transport: the high-water latch is a verdict
        // about the study that tripped it, and the fetched-chunk memo describes a
        // dataset cache that is purged on the same study change.
        resetBundleTransport();
        this.isStopped = false;
        this.isPaused = false;
        this.queue = [];
        this.activeSeriesUID = null;
        this.isProcessing = false;
        this.statusCallbacks.clear();
        this.seriesImageIds.clear();
        this.lookaheadSeries.clear();
        this.seriesFocusIndex.clear();
        this.seriesAttempted.clear();
        this.trailingLoads.clear();
        this.lastInteractionTime = 0;
    }
    /**
     * Get queue info for debugging
     */
    getQueueInfo() {
        return {
            queue: [...this.queue],
            active: this.activeSeriesUID,
        };
    }
}
// Export singleton instance
export const prefetchManager = new PrefetchManager();
/**
 * Compute the Tier 2 look-ahead order for the unopened series of a study.
 *
 * This is the single place that decides *what loads next* once the open
 * canvases are cached — tweak ordering policy here.
 *
 * Policy: prefer the series nearest the focused viewport's series in
 * series-strip order, expanding outward (next, prev, next+1, prev-1, …), so a
 * radiologist paging to the adjacent reconstruction finds it already loaded.
 * Visible series are excluded (they're Tier 0/1). With no focus, falls back to
 * plain strip order.
 *
 * @param seriesIdsInStripOrder All study series ids, in series-strip order.
 * @param visibleSeriesIds      Series currently assigned to a viewport.
 * @param focusedSeriesId       Series in the active viewport (or null).
 */
export function computeLookaheadOrder(seriesIdsInStripOrder, visibleSeriesIds, focusedSeriesId) {
    const nonVisible = seriesIdsInStripOrder.filter((id) => !visibleSeriesIds.has(id));
    if (nonVisible.length <= 1)
        return nonVisible;
    const focusIdx = focusedSeriesId ? seriesIdsInStripOrder.indexOf(focusedSeriesId) : -1;
    if (focusIdx < 0)
        return nonVisible;
    const ordered = [];
    for (let d = 1; d < seriesIdsInStripOrder.length; d++) {
        const next = seriesIdsInStripOrder[focusIdx + d];
        if (next && !visibleSeriesIds.has(next))
            ordered.push(next);
        const prev = seriesIdsInStripOrder[focusIdx - d];
        if (prev && !visibleSeriesIds.has(prev))
            ordered.push(prev);
    }
    return ordered;
}
